import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { HistoryFacade } from '../history-entries';
import { WorkspaceFacade } from '../workspace';
import { ChatMessage, KanbanColumn } from './tickets.models';
import { TicketsActions } from './tickets.actions';
import {
  selectHasBoard,
  selectTicketsBoard,
  selectTicketsChatMessages,
  selectTicketsDirty,
  selectTicketsError,
  selectTicketsExecutionModelId,
  selectTicketsLastSavedAt,
  selectTicketsLoading,
  selectTicketsProjectDefinition,
  selectTicketsProjectDiagram,
  selectTicketsProjectSchema,
  selectTicketsRepoContext,
  selectTicketsSaveError,
  selectTicketsSaving,
} from './tickets.selectors';

@Injectable({ providedIn: 'root' })
export class TicketsFacade {
  private store = inject(Store);
  private workspace = inject(WorkspaceFacade);
  private history = inject(HistoryFacade);

  readonly board = toSignal(this.store.select(selectTicketsBoard), { requireSync: true });
  readonly hasBoard = toSignal(this.store.select(selectHasBoard), { requireSync: true });
  readonly chatMessages = toSignal(this.store.select(selectTicketsChatMessages), { requireSync: true });
  readonly projectDefinition = toSignal(this.store.select(selectTicketsProjectDefinition), { requireSync: true });
  readonly projectSchema = toSignal(this.store.select(selectTicketsProjectSchema), { requireSync: true });
  readonly projectDiagram = toSignal(this.store.select(selectTicketsProjectDiagram), { requireSync: true });
  readonly repoContext = toSignal(this.store.select(selectTicketsRepoContext), { requireSync: true });
  readonly dirty = toSignal(this.store.select(selectTicketsDirty), { requireSync: true });
  readonly saving = toSignal(this.store.select(selectTicketsSaving), { requireSync: true });
  readonly saveError = toSignal(this.store.select(selectTicketsSaveError), { requireSync: true });
  readonly lastSavedAt = toSignal(this.store.select(selectTicketsLastSavedAt), { requireSync: true });
  readonly executionModelId = toSignal(this.store.select(selectTicketsExecutionModelId), { requireSync: true });
  readonly loading = toSignal(this.store.select(selectTicketsLoading), { requireSync: true });
  readonly error = toSignal(this.store.select(selectTicketsError), { requireSync: true });
  readonly activeProjectId = this.workspace.activeProjectId;

  load(): void {
    this.store.dispatch(TicketsActions.load());
  }
  refreshBoard(projectId?: string): void {
    this.store.dispatch(TicketsActions.refreshBoard({ projectId }));
  }
  setBoardDraft(columns: KanbanColumn[]): void {
    this.store.dispatch(TicketsActions.setBoardDraft({ columns }));
  }
  saveBoard(): void {
    this.store.dispatch(TicketsActions.saveBoard());
  }
  setExecutionModelId(modelId: string): void {
    this.store.dispatch(TicketsActions.setExecutionModelId({ modelId }));
  }
  setChatMessages(messages: ChatMessage[]): void {
    this.store.dispatch(TicketsActions.setChatMessages({ messages }));
  }
  async logChatEvent(text: string, kind: 'user' | 'assistant' | 'info' | 'update' | 'error' = 'info'): Promise<void> {
    const projectId = this.activeProjectId();
    if (!projectId) return;
    await this.history.add(projectId, 'tickets.chat', text, kind as any);
  }
}
